import React, { useState } from 'react';
import { PlusIcon, Edit2Icon, Trash2Icon, SaveIcon, XIcon, ArrowUpIcon, ArrowDownIcon, PinIcon } from 'lucide-react';
import { useMusic } from '../../lib/cmsStore';
import { MusicItem } from '../../lib/cmsTypes';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Select } from '../../components/ui/Select';
import { VersionHistoryPanel, VersionHistoryButton } from './components/VersionHistoryPanel';
export function MusicEditor() {
  const {
    items,
    saveItem,
    deleteItem,
    reorderItems,
    isLoading
  } = useMusic();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<MusicItem | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  if (isLoading) return <div className="p-8 font-mono text-sm" data-id="element-1357">Loading...</div>;
  // Sort items by order for display
  const sortedItems = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
  const handleEdit = (item: MusicItem) => {
    setEditForm({
      ...item
    });
    setEditingId(item.id);
  };
  const handleCreate = () => {
    const newItem: MusicItem = {
      id: Math.random().toString(36).substr(2, 9),
      title: 'New Release',
      subtitle: '',
      coverArtwork: '',
      description: '',
      releaseDate: new Date().toISOString().split('T')[0],
      genre: 'Electronic',
      mood: [],
      tags: [],
      audioEmbed: '',
      streamingLinks: [],
      credits: '',
      lyrics: '',
      featured: false,
      status: 'draft',
      slug: `new-release-${Date.now()}`,
      seoTitle: '',
      seoDescription: '',
      order: items.length,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setEditForm(newItem);
    setEditingId(newItem.id);
  };
  const handleSave = () => {
    if (editForm) {
      saveItem({
        ...editForm,
        updatedAt: new Date().toISOString()
      });
      setEditingId(null);
      setEditForm(null);
    }
  };
  const updateField = (field: keyof MusicItem, value: any) => {
    setEditForm(prev => prev ? {
      ...prev,
      [field]: value
    } : prev);
  };
  const togglePin = (item: MusicItem) => {
    saveItem({
      ...item,
      featured: !item.featured,
      updatedAt: new Date().toISOString()
    });
  };
  const moveItem = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0 || direction === 'down' && index === sortedItems.length - 1) {
      return;
    }
    const newItems = [...sortedItems];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    // Swap items
    const temp = newItems[index];
    newItems[index] = newItems[targetIndex];
    newItems[targetIndex] = temp;
    // Update order values
    const reorderedItems = newItems.map((item, i) => ({
      ...item,
      order: i
    }));
    reorderItems(reorderedItems);
  };
  return <div className="p-8 max-w-7xl mx-auto" data-id="element-1358">
      <div className="flex items-center justify-between mb-8" data-id="element-1359">
        <div data-id="element-1360">
          <h1 className="font-display text-3xl font-bold tracking-tight" data-id="element-1361">
            Music
          </h1>
          <p className="font-mono text-sm text-muted-foreground mt-2" data-id="element-1362">
            Manage your audio releases and experiments.
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-1363">
          <VersionHistoryButton onClick={() => setHistoryOpen(true)} data-id="element-1364" />
          {!editingId && <Button onClick={handleCreate} className="gap-2" data-id="element-1365">
              <PlusIcon className="w-4 h-4" data-id="element-1366" /> New Release
            </Button>}
        </div>
      </div>

      {editingId && editForm ? <div className="border border-border bg-card p-6 md:p-8 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-1367">
          <div className="flex items-center justify-between border-b border-border pb-4" data-id="element-1368">
            <h2 className="font-display text-2xl font-bold" data-id="element-1369">Edit Release</h2>
            <div className="flex gap-2" data-id="element-1370">
              <Button variant="ghost" onClick={() => setEditingId(null)} data-id="element-1371">
                <XIcon className="w-4 h-4 mr-2" data-id="element-1372" /> Cancel
              </Button>
              <Button onClick={handleSave} data-id="element-1373">
                <SaveIcon className="w-4 h-4 mr-2" data-id="element-1374" /> Save
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1375">
            <Input label="Title" value={editForm.title} onChange={e => updateField('title', e.target.value)} data-id="element-1376" />
            <Input label="Slug" value={editForm.slug} onChange={e => updateField('slug', e.target.value)} data-id="element-1377" />
            <Input label="Genre" value={editForm.genre} onChange={e => updateField('genre', e.target.value)} placeholder="e.g. Electronic" data-id="element-1378" />
            <Input label="Mood (comma-separated)" value={(editForm.mood || []).join(', ')} onChange={e => updateField('mood', e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean))} placeholder="e.g. Calm, Atmospheric, Dark" data-id="element-1379" />
            <Input label="Tags (comma-separated)" value={(editForm.tags || []).join(', ')} onChange={e => updateField('tags', e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean))} placeholder="e.g. Electronic, Experimental, Ambient" data-id="element-1380" />
            <Select label="Status" value={editForm.status} onChange={e => updateField('status', e.target.value)} options={[{
          label: 'Draft',
          value: 'draft'
        }, {
          label: 'Published',
          value: 'published'
        }]} data-id="element-1381" />
            <div className="flex items-center h-10 mt-6" data-id="element-1382">
              <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-foreground cursor-pointer" data-id="element-1383">
                <input type="checkbox" checked={editForm.featured} onChange={e => updateField('featured', e.target.checked)} className="accent-primary w-4 h-4" data-id="element-1384" />
                Pin to top
              </label>
            </div>
          </div>

          <Textarea label="Description" value={editForm.description} onChange={e => updateField('description', e.target.value)} rows={3} data-id="element-1385" />
          <Textarea label="Audio Embed Code (SoundCloud/Spotify iframe)" value={editForm.audioEmbed} onChange={e => updateField('audioEmbed', e.target.value)} rows={3} className="font-mono text-xs" data-id="element-1386" />

          {/* Streaming Links */}
          <div className="space-y-4" data-id="element-1387">
            <div className="flex items-center justify-between border-b border-border pb-2" data-id="element-1388">
              <h3 className="font-display text-lg font-bold" data-id="element-1389">
                Streaming Links
              </h3>
              <Button size="sm" variant="outline" onClick={() => updateField('streamingLinks', [...(editForm.streamingLinks || []), {
            platform: '',
            url: ''
          }])} data-id="element-1390">
                <PlusIcon className="w-4 h-4 mr-2" data-id="element-1391" /> Add Link
              </Button>
            </div>

            {/* Quick-add buttons */}
            <div className="flex flex-wrap gap-2" data-id="element-1392">
              {['Spotify', 'SoundCloud', 'Apple Music', 'YouTube Music', 'Bandcamp', 'Tidal'].map(p => {
            const exists = (editForm.streamingLinks || []).some(l => l.platform.toLowerCase() === p.toLowerCase());
            if (exists) return null;
            return <button key={p} onClick={() => updateField('streamingLinks', [...(editForm.streamingLinks || []), {
              platform: p,
              url: ''
            }])} className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors" data-id="element-1393">
                    + {p}
                  </button>;
          })}
            </div>

            <div className="space-y-3" data-id="element-1394">
              {(editForm.streamingLinks || []).map((link, i) => <div key={i} className="flex items-center gap-3 bg-background border border-border p-3" data-id="element-1395">
                  <Input value={link.platform} onChange={e => {
              const updated = [...(editForm.streamingLinks || [])];
              updated[i] = {
                ...updated[i],
                platform: e.target.value
              };
              updateField('streamingLinks', updated);
            }} placeholder="Platform (e.g. Spotify)" className="w-1/3" data-id="element-1396" />
                  <Input value={link.url} onChange={e => {
              const updated = [...(editForm.streamingLinks || [])];
              updated[i] = {
                ...updated[i],
                url: e.target.value
              };
              updateField('streamingLinks', updated);
            }} placeholder="https://open.spotify.com/..." className="flex-1" data-id="element-1397" />
                  <Button variant="ghost" size="sm" onClick={() => {
              const updated = [...(editForm.streamingLinks || [])];
              updated.splice(i, 1);
              updateField('streamingLinks', updated);
            }} className="text-destructive ml-2" data-id="element-1398">
                    <Trash2Icon className="w-4 h-4" data-id="element-1399" />
                  </Button>
                </div>)}
              {(editForm.streamingLinks || []).length === 0 && <p className="font-mono text-xs text-muted-foreground py-4 text-center border border-dashed border-border" data-id="element-1400">
                  No streaming links yet. Add Spotify, SoundCloud, or other
                  platforms above.
                </p>}
            </div>
          </div>
        </div> : <div className="bg-card border border-border" data-id="element-1401">
          <div className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-4 p-4 border-b border-border font-mono text-xs uppercase tracking-widest text-muted-foreground" data-id="element-1402">
            <div className="w-16" data-id="element-1403">Order</div>
            <div data-id="element-1404">Release</div>
            <div className="w-32" data-id="element-1405">Genre</div>
            <div className="w-24" data-id="element-1406">Status</div>
            <div className="w-12 text-center" data-id="element-1407">Pin</div>
            <div className="w-32 text-right" data-id="element-1408">Actions</div>
          </div>

          <div className="divide-y divide-border" data-id="element-1409">
            {sortedItems.length === 0 ? <div className="p-8 text-center text-muted-foreground font-mono text-sm" data-id="element-1410">
                No music found.
              </div> : sortedItems.map((item, index) => <div key={item.id} className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-4 p-4 items-center hover:bg-secondary/50 transition-colors" data-id="element-1411">
                  <div className="w-16 flex flex-col gap-1" data-id="element-1412">
                    <button onClick={() => moveItem(index, 'up')} disabled={index === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-1413">
                      <ArrowUpIcon className="w-4 h-4" data-id="element-1414" />
                    </button>
                    <button onClick={() => moveItem(index, 'down')} disabled={index === sortedItems.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-1415">
                      <ArrowDownIcon className="w-4 h-4" data-id="element-1416" />
                    </button>
                  </div>
                  <div data-id="element-1417">
                    <h3 className="font-display text-lg font-bold" data-id="element-1418">
                      {item.title}
                    </h3>
                    <p className="font-mono text-xs text-muted-foreground mt-1 truncate max-w-md" data-id="element-1419">
                      {item.description}
                    </p>
                  </div>
                  <div className="w-32 font-mono text-xs text-muted-foreground" data-id="element-1420">
                    {item.genre}
                  </div>
                  <div className="w-24 flex flex-col gap-1" data-id="element-1421">
                    <span className={`inline-flex items-center justify-center px-2 py-1 font-mono text-[10px] uppercase tracking-widest border ${item.status === 'published' ? 'border-primary/50 text-primary bg-primary/10' : 'border-border text-muted-foreground bg-secondary'}`} data-id="element-1422">
                      {item.status}
                    </span>
                  </div>
                  <div className="w-12 flex justify-center" data-id="element-1423">
                    <button onClick={() => togglePin(item)} className={`p-1.5 rounded-md transition-colors ${item.featured ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:bg-secondary'}`} title={item.featured ? 'Unpin' : 'Pin to top'} data-id="element-1424">
                      <PinIcon className="w-4 h-4" data-id="element-1425" />
                    </button>
                  </div>
                  <div className="w-32 flex justify-end gap-2" data-id="element-1426">
                    <Button variant="ghost" size="sm" className="px-2" onClick={() => handleEdit(item)} title="Edit" data-id="element-1427">
                      <Edit2Icon className="w-4 h-4" data-id="element-1428" />
                    </Button>
                    <Button variant="ghost" size="sm" className="px-2 text-destructive hover:text-destructive" onClick={() => {
              if (window.confirm('Delete this release?')) deleteItem(item.id);
            }} title="Delete" data-id="element-1429">
                      <Trash2Icon className="w-4 h-4" data-id="element-1430" />
                    </Button>
                  </div>
                </div>)}
          </div>
        </div>}
      <VersionHistoryPanel contentKey="sinka_music" isOpen={historyOpen} onClose={() => setHistoryOpen(false)} onRestore={data => {
      if (Array.isArray(data)) reorderItems(data);
    }} data-id="element-1431" />
    </div>;
}